// Full-screen dot animation for the outcome of a user action: a dotted disc with a mark
// grows out of the screen center and spreads to the corners as a wave of dots.
// It lives on its own canvas in <body>, so it survives route changes and never blocks clicks.

const DURATION = 1600;
const DISC_MS = 340;
const MARK_START = 260;
const MARK_MS = 340;
const WAVE_START = 300;
const WAVE_MS = 1050;
const SECOND_WAVE_DELAY = 200;
const SECOND_WAVE_STRENGTH = 0.55;
const SHAKE_START = 340;
const SHAKE_MS = 480;
const EXIT_START = 1180;

const DISC_RADIUS = 72;
const ICON_SPACING = 5;
const ICON_DOT_RADIUS = 2.35;
const MARK_THICKNESS = 0.15; // share of the disc radius
const WAVE_SPACING = 20;
const WAVE_LEAD = 46; // dots start swelling this far ahead of the front
const WAVE_WIDTH = 190;
const WAVE_DOT_RADIUS = 4.6;
const MAX_WAVE_DOTS = 12000;

const VARIANTS = {
    success: {
        colorToken: '--color-green',
        fallbackColor: '#4bb543',
        strokes: [[[-0.42, 0.02], [-0.12, 0.32], [0.45, -0.3]]],
    },
    failure: {
        colorToken: '--color-dark-red',
        fallbackColor: '#c53030',
        strokes: [[[-0.36, -0.36], [0.36, 0.36]], [[0.36, -0.36], [-0.36, 0.36]]],
    },
};

const clamp01 = (value) => Math.min(Math.max(value, 0), 1);
const easeOutCubic = (value) => 1 - (1 - value) ** 3;
const easeOutBack = (value) => 1 + 2.2 * (value - 1) ** 3 + 1.2 * (value - 1) ** 2;
const smoothstep = (from, to, value) => {
    const position = clamp01((value - from) / (to - from));

    return position * position * (3 - 2 * position);
};
const pseudoRandom = (x, y) => {
    const value = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;

    return value - Math.floor(value);
};

// The closest point of the mark to a dot: `at` is 0..1 along the drawing order, `distance` is in disc radii
const getMarkPosition = (x, y, strokes) => {
    const segments = strokes.flatMap((stroke) => stroke.slice(1).map((end, index) => [stroke[index], end]));
    const totalLength = segments.reduce((sum, [a, b]) => sum + Math.hypot(b[0] - a[0], b[1] - a[1]), 0);

    let passed = 0;
    let at = 0;
    let closestDistance = Infinity;

    segments.forEach(([a, b]) => {
        const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
        const along = clamp01(((x - a[0]) * (b[0] - a[0]) + (y - a[1]) * (b[1] - a[1])) / (length * length));
        const distance = Math.hypot(x - (a[0] + (b[0] - a[0]) * along), y - (a[1] + (b[1] - a[1]) * along));

        if (distance < closestDistance) {
            closestDistance = distance;
            at = (passed + along * length) / totalLength;
        }

        passed += length;
    });

    return {at, distance: closestDistance};
};

const createScene = (type, width, height) => {
    const variant = VARIANTS[type] || VARIANTS.success;
    const color = getComputedStyle(document.documentElement).getPropertyValue(variant.colorToken).trim()
        || variant.fallbackColor;
    const centerX = width / 2;
    const centerY = height / 2;
    const discRadius = Math.min(DISC_RADIUS, Math.min(width, height) * 0.2);

    const iconDots = [];
    const rowHeight = ICON_SPACING * 0.87;

    for (let row = -Math.ceil(discRadius / rowHeight); row * rowHeight <= discRadius; row += 1) {
        const shift = row % 2 ? ICON_SPACING / 2 : 0; // staggered rows read as a halftone

        for (let col = -Math.ceil(discRadius / ICON_SPACING) - 1; col * ICON_SPACING <= discRadius; col += 1) {
            const x = col * ICON_SPACING + shift;
            const y = row * rowHeight;
            const reach = Math.hypot(x, y) / discRadius;

            if (reach <= 1) {
                const mark = getMarkPosition(x / discRadius, y / discRadius, variant.strokes);

                iconDots.push({
                    x,
                    y,
                    reach,
                    seed: pseudoRandom(x + 1, y + 1),
                    // Dots shrink towards the rim and towards the mark, which keeps both edges smooth
                    edgeSize: 0.45 + 0.55 * smoothstep(1, 0.86, reach),
                    markAt: mark.at,
                    markSize: smoothstep(MARK_THICKNESS * 0.75, MARK_THICKNESS * 1.5, mark.distance),
                });
            }
        }
    }

    const maxDistance = Math.hypot(centerX, centerY);
    const spacing = Math.max(WAVE_SPACING, Math.sqrt((width * height) / MAX_WAVE_DOTS));
    const waveRowHeight = spacing * 0.87;
    const waveDots = [];
    let waveRow = 0;

    for (let y = centerY % waveRowHeight; y < height; y += waveRowHeight) {
        const shift = waveRow % 2 ? spacing / 2 : 0;

        for (let x = (centerX % spacing) + shift - spacing; x < width; x += spacing) {
            const distance = Math.hypot(x - centerX, y - centerY);

            if (distance > discRadius) {
                waveDots.push({x, y, distance, seed: pseudoRandom(x, y)});
            }
        }

        waveRow += 1;
    }

    return {type, width, height, color, centerX, centerY, discRadius, iconDots, waveDots, maxDistance};
};

const drawWave = (ctx, scene, elapsed, waveStrength) => {
    const progress = clamp01(elapsed / WAVE_MS);

    if (progress <= 0 || progress >= 1) {
        return;
    }

    const isFailure = scene.type === 'failure';
    const front = easeOutCubic(progress) * (scene.maxDistance + WAVE_WIDTH);
    const fade = (1 - progress * 0.4) * waveStrength;

    scene.waveDots.forEach((dot) => {
        // The failure wave has a jagged front, the success wave a clean circle
        const lag = front - dot.distance - (isFailure ? dot.seed * 44 : 0);

        if (lag < -WAVE_LEAD || lag > WAVE_WIDTH) {
            return;
        }

        const strength = lag < 0 ? 1 + lag / WAVE_LEAD : (1 - lag / WAVE_WIDTH) ** 1.4;
        const trail = lag < 0 ? 0 : lag / WAVE_WIDTH;
        // Behind the front the success dots float up and the failure dots fall down
        const drift = isFailure ? trail * trail * 54 : -trail * 18;
        const radius = 0.5 + strength * WAVE_DOT_RADIUS;
        const y = dot.y + drift;

        // White halo keeps the dots readable on dark and saturated backgrounds
        ctx.globalAlpha = strength * 0.5 * fade;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(dot.x, y, radius + 1.4, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = strength * 0.8 * fade;
        ctx.fillStyle = scene.color;
        ctx.beginPath();
        ctx.arc(dot.x, y, radius, 0, Math.PI * 2);
        ctx.fill();
    });
};

const drawIcon = (ctx, scene, elapsed) => {
    if (elapsed <= 0) {
        return;
    }

    const isFailure = scene.type === 'failure';
    const exit = clamp01((elapsed - EXIT_START) / (DURATION - EXIT_START));
    const markProgress = clamp01((elapsed - MARK_START) / MARK_MS);
    const shakeProgress = clamp01((elapsed - SHAKE_START) / SHAKE_MS);
    const shake = isFailure && shakeProgress < 1
        ? Math.sin(shakeProgress * Math.PI * 6) * 9 * (1 - shakeProgress)
        : 0;
    const backingScale = easeOutBack(clamp01(elapsed / DISC_MS));

    ctx.save();
    ctx.translate(scene.centerX + shake, scene.centerY);

    // White backing keeps the mark readable over tables and other busy content; it goes first on exit
    ctx.save();
    ctx.globalAlpha = clamp01(1 - exit * 3);
    ctx.shadowColor = 'rgba(15, 23, 42, 0.22)';
    ctx.shadowBlur = 26;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, 0, (scene.discRadius + 5) * backingScale, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.globalAlpha = 1 - exit * exit;
    ctx.fillStyle = scene.color;
    ctx.beginPath();
    scene.iconDots.forEach((dot) => {
        // Each dot pops in on its own, from the center to the rim
        const appear = easeOutBack(clamp01((elapsed - dot.reach * DISC_MS * 0.6) / (DISC_MS * 0.4)));
        const markReveal = clamp01((markProgress - dot.markAt) / 0.1);
        const markSize = 1 + (dot.markSize - 1) * markReveal;
        let {x, y} = dot;
        let size = appear * dot.edgeSize * markSize;

        if (exit > 0) {
            const push = exit * exit;

            if (isFailure) {
                // The disc crumbles and falls
                x += (dot.seed - 0.5) * 36 * exit;
                y += push * (46 + dot.seed * 130);
                size *= 1 - exit * 0.6;
            } else {
                // The disc bursts outwards and joins the wave
                const burst = 1 + push * (0.7 + dot.seed * 1.1);

                x *= burst;
                y *= burst;
                size *= 1 - exit * 0.75;
            }
        }

        const radius = ICON_DOT_RADIUS * size;

        if (radius > 0.15) {
            ctx.moveTo(x + radius, y);
            ctx.arc(x, y, radius, 0, Math.PI * 2);
        }
    });
    ctx.fill();
    ctx.restore();
};

const drawFrame = (ctx, scene, elapsed) => {
    ctx.clearRect(0, 0, scene.width, scene.height);

    drawWave(ctx, scene, elapsed - WAVE_START, 1);
    drawWave(ctx, scene, elapsed - WAVE_START - SECOND_WAVE_DELAY, SECOND_WAVE_STRENGTH);

    ctx.globalAlpha = 1;
    drawIcon(ctx, scene, elapsed);
};

const createCanvas = (width, height) => {
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = document.createElement('canvas');

    canvas.width = width * pixelRatio;
    canvas.height = height * pixelRatio;
    canvas.setAttribute('aria-hidden', 'true');
    // Above antd modals (1000), below antd messages (2010)
    canvas.style.cssText = `position:fixed;inset:0;width:${width}px;height:${height}px;z-index:1500;pointer-events:none;`;
    document.body.appendChild(canvas);
    canvas.getContext('2d').scale(pixelRatio, pixelRatio);

    return canvas;
};

let stopCurrent = null;

// type: 'success' | 'failure'. Meant for finished user actions (save, create, delete),
// not for failed data loads. Keep the message/notification too: it carries the text.
export const playFeedbackWave = (type = 'success') => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    if (stopCurrent) {
        stopCurrent();
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');
    const scene = createScene(type, width, height);
    let frameId = null;
    let startTime = null;

    const stop = () => {
        cancelAnimationFrame(frameId);
        canvas.remove();
        stopCurrent = null;
    };

    const tick = (now) => {
        startTime = startTime ?? now;
        const elapsed = now - startTime;

        if (elapsed >= DURATION) {
            stop();
            return;
        }

        drawFrame(ctx, scene, elapsed);
        frameId = requestAnimationFrame(tick);
    };

    stopCurrent = stop;
    frameId = requestAnimationFrame(tick);
};
